/**
 * **対象の照準の枠**（Issue #571。指示書 `../direction/become_target.md`）。`effects/become_target.js` が盤に置き、
 * `drawObjects.js::targetSight` が描く（帯の中の絵は無い。締まる動きも脈も**置かれてからの時間の関数**で、印そのものが描く）。
 *
 * - 対象のカードの周りに**四隅の括弧**。イラストには掛けない（カードの外側に隙間を空ける）
 * - 0〜200ms: 外から締まる（最後に 2 フレームだけ白く硬く光る）/ 200〜500ms: 2 回脈打つ / 以後: ゆっくり控えめに脈打ち続ける
 * - **入れ子**: 同じカードに複数のリンクの枠があるときは、後のリンク（番号の大きいもの）ほど外側。角に小さくチェーンの番号
 * - 色は対象を取ったプレイヤーの色（鋼の黒い縁取りの上に色の芯と白い細線。ぼんやりした光は使わない）
 *
 * 座標は**舞台のピクセル**。カードの矩形は平面の座標（ゾーンの中心・カードの大きさ・向き）で広げてから投影する
 * （舞台のピクセルで広げると遠近の付いた後の形を広げることになる）。three も canvas も import しない。
 */
import { CARD_ASPECT } from '../../../core/field/zones';
import { seg, tween } from '../../../core/effect/motion';
import { rgba } from '../../../canvas/paint';
import { FONT } from './common';

/** 時刻（置かれてからの ms） */
export const SNAP = 200;       // 締まり終わる
export const SETTLE = 500;     // 最初の脈が終わる
/** 締まる前の広がり（枠の大きさの倍率） */
export const FROM = 1.45;
/** カードの縁から枠までの隙間（カードの大きさの比）と、入れ子 1 段ぶんの広がり */
export const GAP = 0.1;
export const STEP = 0.12;
/** 入れ子の段の上限（それより外は同じ段に重ねる） */
export const MAX_LEVEL = 3;
/** 括弧の腕の長さ（辺の比） */
const ARM = 0.3;
/** 以後の脈の周期と振れ幅 */
const PERIOD = 1400;

/** 場（照準の枠を出す場所）か */
export const isFieldZone = (zone) => /^(monster|magic_trap|field):/.test(String(zone ?? ''));

/**
 * 置かれてからの時間 → 見え方 `{ k: 大きさの倍率, alpha, flash: 白くする量(0..1), line: 線の太さの倍率 }`。
 */
export function sightMotion(local) {
  const t = Number.isFinite(local) ? local : SETTLE;
  if (t < 0) return { k: FROM, alpha: 0, flash: 0, line: 1 };
  if (t < SNAP) {
    return {
      k: FROM - (FROM - 1) * tween(t, 0, SNAP, 'power3_out'),
      alpha: seg(t, 0, 60),
      flash: t >= SNAP - 34 ? 1 : 0,
      line: 1,
    };
  }
  if (t < SETTLE) {
    // 2 回脈打つ（2 回目は小さく）
    const u = seg(t, SNAP, SETTLE);
    const bump = Math.abs(Math.sin(u * Math.PI * 2)) * (u < 0.5 ? 1 : 0.55);
    return { k: 1 + 0.045 * bump, alpha: 1, flash: t < SNAP + 34 ? 1 : 0, line: 1 + 0.25 * bump };
  }
  // 以後: ゆっくり控えめに
  const w = 0.5 - 0.5 * Math.cos(((t - SETTLE) / PERIOD) * Math.PI * 2);
  return { k: 1 + 0.015 * w, alpha: 0.82 + 0.18 * w, flash: 0, line: 1 };
}

/** 入れ子の段: 同じカードに置かれている照準のうち、自分より番号の小さいリンクの数 */
export function sightLevel(placed, uid, chain) {
  const below = new Set();
  (placed ?? []).forEach((s) => {
    if (s?.mark !== 'targetSight') return;
    const d = s.data ?? {};
    if (d.uid !== uid || !(Number(d.chain) < Number(chain))) return;
    below.add(Number(d.chain));
  });
  return Math.min(MAX_LEVEL, below.size);
}

/** 段 → 枠の大きさ（カードに対する倍率。締まったとき） */
export const levelScale = (level) => 1 + GAP + Math.max(0, Math.min(MAX_LEVEL, level)) * STEP;

/**
 * 枠の 4 点（舞台のピクセル。左上・右上・右下・左下の順＝カードの向きのまま）。
 *
 * @param view 盤の見え方（`zonePlane` / `toStage` / `cardAngle` / `unit`）
 * @param zone ゾーンのキー
 * @param turn カードの状態の回し（守備表示 +90°）
 * @param scale カードに対する倍率
 */
export function sightQuad(view, zone, turn, scale) {
  const plane = view?.zonePlane?.(zone);
  if (!Array.isArray(plane) || plane.length !== 4 || typeof view?.toStage !== 'function') return null;
  const cx = plane.reduce((a, p) => a + p[0], 0) / 4;
  const cy = plane.reduce((a, p) => a + p[1], 0) / 4;
  const unit = view.unit > 0 ? view.unit : Math.hypot(plane[1][0] - plane[0][0], plane[1][1] - plane[0][1]);
  const hw = (unit * scale) / 2;
  const hh = (unit / CARD_ASPECT) * scale / 2;
  const a = (view.cardAngle?.(zone) ?? 0) + (turn ?? 0);
  const c = Math.cos(a); const s = Math.sin(a);
  const out = [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]].map(([x, y]) => view.toStage([cx + x * c - y * s, cy + x * s + y * c]));
  if (!out.every((p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]))) return null;
  return out;
}

/** 4 点の外接する箱（余白つき） */
export function quadBox(quad, pad = 0) {
  const xs = quad.map((p) => p[0]); const ys = quad.map((p) => p[1]);
  const x = Math.min(...xs) - pad; const y = Math.min(...ys) - pad;
  return { x, y, w: Math.max(...xs) + pad - x, h: Math.max(...ys) + pad - y };
}

/** 括弧の線（角ごとに 2 本の腕）: `[[角, 腕の先1, 腕の先2], …]` */
export function bracketArms(quad) {
  return quad.map((p, i) => {
    const prev = quad[(i + 3) % 4]; const next = quad[(i + 1) % 4];
    return [
      [p[0] + (prev[0] - p[0]) * ARM, p[1] + (prev[1] - p[1]) * ARM],
      p,
      [p[0] + (next[0] - p[0]) * ARM, p[1] + (next[1] - p[1]) * ARM],
    ];
  });
}

/** 同じカードに置かれている照準の一番外の段（リンクの数 − 1） */
export function sightDepth(placed, uid) {
  const chains = new Set();
  (placed ?? []).forEach((s) => {
    if (s?.mark === 'targetSight' && s.data?.uid === uid) chains.add(Number(s.data.chain));
  });
  return Math.min(MAX_LEVEL, Math.max(0, chains.size - 1));
}

/** 番号の札の大きさ（文字の大きさ → 幅・高さ。2 桁は少し広い） */
export const badgeSize = (font, chain) => ({ w: font * (String(chain ?? '').length > 1 ? 1.75 : 1.3), h: font * 1.1 });

/**
 * 番号の札の左上（舞台のピクセル）。**一番外の枠の右上の角の上に、チェーンの番号の順に左から横一列**
 * （角ごとに置くと、外の枠の括弧が内の枠の札を横切って番号が隠れる）。
 *
 * @param outer 一番外の枠の 4 点（締まったとき）
 * @param level 自分の段 / depth 一番外の段
 */
export function badgeSpot(outer, level, depth, font, chain) {
  const corner = outer.reduce((best, p) => (p[0] - p[1] > best[0] - best[1] ? p : best), outer[0]);
  const { w, h } = badgeSize(font, chain);
  const step = font * 1.3 + font * 0.2;
  return [corner[0] - w * 0.35 - (depth - level) * step, corner[1] - h - font * 0.2];
}

/**
 * 枠を 2D の context に塗る（座標は箱の左上が 0）。
 *
 * @param quad 枠の 4 点（舞台のピクセル）
 * @param ox 箱の左上
 * @param opts `{ color, width, flash, chain, badge, spot }`（`badge` = 番号の文字の大きさ。0 なら出さない。`spot` = 札の左上＝`badgeSpot`）
 */
export function paintSight(ctx, quad, ox, oy, {
  color, width, flash = 0, chain = null, badge = 0, spot = null,
}) {
  const pts = quad.map(([x, y]) => [x - ox, y - oy]);
  const arms = bracketArms(pts);
  const path = () => {
    ctx.beginPath();
    arms.forEach(([a, c, b]) => { ctx.moveTo(a[0], a[1]); ctx.lineTo(c[0], c[1]); ctx.lineTo(b[0], b[1]); });
  };
  ctx.lineJoin = 'miter';
  ctx.lineCap = 'butt';
  // 鋼の黒い縁取り → 色の芯 → 白い細線（硬い形だけ。ぼかさない）
  path(); ctx.strokeStyle = 'rgba(6,8,14,0.92)'; ctx.lineWidth = width * 1.9; ctx.stroke();
  path(); ctx.strokeStyle = flash > 0 ? '#ffffff' : color; ctx.lineWidth = width; ctx.stroke();
  path(); ctx.strokeStyle = rgba('#ffffff', flash > 0 ? 1 : 0.75); ctx.lineWidth = Math.max(1, width * 0.22); ctx.stroke();
  if (!(badge > 0) || chain == null || !spot) return;
  // 小さくチェーンの番号（斜めに切った札）
  const text = String(chain);
  const { w, h } = badgeSize(badge, chain);
  const sk = h * 0.28;
  const x = spot[0] - ox; const y = spot[1] - oy;
  ctx.beginPath();
  ctx.moveTo(x + sk, y); ctx.lineTo(x + w, y); ctx.lineTo(x + w - sk, y + h); ctx.lineTo(x, y + h); ctx.closePath();
  ctx.fillStyle = '#0a0c12'; ctx.fill();
  ctx.strokeStyle = color; ctx.lineWidth = Math.max(2, width * 0.5); ctx.stroke();
  ctx.font = `900 ${badge}px ${FONT}`;
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x + w / 2, y + h / 2 + badge * 0.04);
}
